import { FC, useEffect, useMemo, useState } from 'react'
import { Icon, useToast } from '@chakra-ui/react'
import { FiBarChart2, FiDownload } from 'react-icons/fi'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Page from '../../components/Page'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import PaywallModal from '../../components/PaywallModal'
import { AppPageContent, AppPageHeader } from '../../common/components/AppPage'
import {
  REPORTS_NAV_COLLAPSED_STORAGE_KEY,
  SidePanelLayout,
  SidePanelNav,
  useIsDesktopNav,
  useSidePanelNavCollapse,
} from '../../common/components/SidePanelNav'
import { useAppSelector } from '../../app/typedReduxHooks'
import { STATUS } from '../../constants'
import { useHasPermission } from '../../hooks/useHasPermission'
import { useHasEntitlement } from '../../pages/OrganizationProfilePage/planEntitlements'
import { Permission } from '../../types/permission'
import { FeatureKey } from '../../types/organization-entitlements'
import { routes } from '../../pages/routes'
import { ReportsTab } from './types'
import { AnalyticsComingSoonPanel } from './components/AnalyticsComingSoonPanel'
import { ExportsPanel } from './components/ExportsPanel'

export const DataReportsPage: FC = () => {
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isDesktopNav = useIsDesktopNav()
  const [isPaywallOpen, setIsPaywallOpen] = useState(false)
  const hasDataExportManagement = useHasPermission(Permission.DATA_EXPORT_MANAGEMENT, {
    includeOwnerSuperAdminFallback: true,
  })
  const hasDataExportEntitlement = useHasEntitlement(FeatureKey.DATA_EXPORTS)
  const planCode = useAppSelector((state) => state.organization.entitlements?.planCode ?? null)
  const organizationFetchStatus = useAppSelector((state) => state.organization.status.fetchById)
  const areEntitlementsLoaded =
    organizationFetchStatus === STATUS.SUCCESS || organizationFetchStatus === STATUS.FAILURE
  const canExportData = hasDataExportManagement && hasDataExportEntitlement

  const requestedTab = searchParams.get('tab')
  const activeTab: ReportsTab = requestedTab === 'analytics' ? 'analytics' : 'exports'

  useEffect(() => {
    if (!requestedTab) {
      setSearchParams({ tab: 'exports' }, { replace: true })
    }
  }, [requestedTab, setSearchParams])

  useEffect(() => {
    if (!areEntitlementsLoaded) {
      return
    }

    if (activeTab === 'exports' && hasDataExportManagement && !hasDataExportEntitlement) {
      setIsPaywallOpen(true)
      return
    }

    if (hasDataExportEntitlement) {
      setIsPaywallOpen(false)
    }
  }, [activeTab, areEntitlementsLoaded, hasDataExportEntitlement, hasDataExportManagement])

  const { isCollapsed, toggleCollapse } = useSidePanelNavCollapse(REPORTS_NAV_COLLAPSED_STORAGE_KEY)

  const navGroups = useMemo(
    () => [
      {
        label: 'Reports',
        items: [
          {
            id: 'analytics',
            label: 'Analytics',
            icon: <Icon as={FiBarChart2} boxSize="15px" />,
            badge: 'Soon',
            action: 'data_reports_tab_analytics' as const,
          },
          {
            id: 'exports',
            label: 'Exports',
            icon: <Icon as={FiDownload} boxSize="15px" />,
            action: 'data_reports_tab_exports' as const,
          },
        ],
      },
    ],
    []
  )

  if (!hasDataExportManagement) {
    return <UnauthorizedPage />
  }

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab })
  }

  const handleExportComplete = (filename: string) => {
    toast({
      title: `Exported ${filename}`,
      status: 'success',
      duration: 2800,
      isClosable: true,
    })
  }

  const handleUpgrade = () => {
    setIsPaywallOpen(false)
    navigate(routes.pricePlan.path)
  }

  return (
    <Page>
      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        onUpgrade={handleUpgrade}
        planCode={planCode}
        feature={FeatureKey.DATA_EXPORTS}
      />
      <AppPageContent>
        <AppPageHeader
          title="Reporting Dashboard"
          subtitle="Prescription analytics and insights"
          icon={<Icon as={FiBarChart2} boxSize="18px" color="white" aria-hidden />}
        />

        <SidePanelLayout
          isCollapsed={isCollapsed}
          isMobileView={!isDesktopNav}
          nav={
            <SidePanelNav
              activeId={activeTab}
              groups={navGroups}
              isCollapsed={isCollapsed}
              onToggleCollapse={toggleCollapse}
              onChange={handleTabChange}
              ariaLabel="Reports navigation"
              isMobileView={!isDesktopNav}
            />
          }
        >
          {activeTab === 'analytics' ? (
            <AnalyticsComingSoonPanel onGoToExports={() => handleTabChange('exports')} />
          ) : (
            <ExportsPanel onExportComplete={handleExportComplete} canExportData={canExportData} />
          )}
        </SidePanelLayout>
      </AppPageContent>
    </Page>
  )
}
